import React, { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { AppButton, AppTextField } from '../design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EfemAdapterAssignment,
  LocationAdapterAssignment,
  OpsIotProvisioningStatus,
} from '@cnapse/shared/schemas.js';
import { PRODUCT_NAMES } from '@cnapse/shared';
import { providerGatewayAdaptersService } from '../../services/ProviderGatewayAdaptersService';
import { opsDevicesService } from '../../services/OpsDevicesService';

type GatewayAdapterKind = 'efem' | 'location';

type DeviceProvisioningSnapshot = {
  iotProvisioningStatus?: OpsIotProvisioningStatus | null;
  thingName?: string | null;
};

interface GatewayAdapterAssignmentPanelProps {
  kind: GatewayAdapterKind;
  serialNumber: string;
  assignment: EfemAdapterAssignment | LocationAdapterAssignment | null | undefined;
  /** When provided, skips a device fetch for the provisioning gate. */
  deviceProvisioning?: DeviceProvisioningSnapshot | null;
  onAssigned?: () => void;
}

function canAssignGatewayAdapter(device: DeviceProvisioningSnapshot | null | undefined): boolean {
  return device?.iotProvisioningStatus === 'provisioned' && !!device.thingName;
}

function adapterKeyFromAssignment(
  kind: GatewayAdapterKind,
  assignment: EfemAdapterAssignment | LocationAdapterAssignment | null | undefined,
): string {
  if (!assignment) return '';
  return kind === 'efem'
    ? (assignment as EfemAdapterAssignment).efemAdapterKey
    : (assignment as LocationAdapterAssignment).locationAdapterKey;
}

export function GatewayAdapterAssignmentPanel({
  kind,
  serialNumber,
  assignment,
  deviceProvisioning,
  onAssigned,
}: GatewayAdapterAssignmentPanelProps) {
  const queryClient = useQueryClient();
  const [adapterKey, setAdapterKey] = useState(adapterKeyFromAssignment(kind, assignment));
  const [notes, setNotes] = useState(assignment?.notes ?? '');

  const assignmentQueryKey = ['gateway-adapter-assignment', kind, serialNumber] as const;

  const assignmentQuery = useQuery({
    queryKey: assignmentQueryKey,
    queryFn: async () => {
      const res = await providerGatewayAdaptersService.getGatewayAdapterAssignment(kind, serialNumber);
      return res.data.assignment;
    },
  });

  const shouldFetchProvisioning = deviceProvisioning === undefined;
  const deviceQuery = useQuery({
    queryKey: ['gateway-device-provisioning', serialNumber],
    queryFn: async () => {
      const res = await opsDevicesService.getDevice(serialNumber);
      return {
        iotProvisioningStatus: res.data.device.iotProvisioningStatus,
        thingName: res.data.device.thingName,
      } satisfies DeviceProvisioningSnapshot;
    },
    enabled: shouldFetchProvisioning && !!serialNumber,
  });

  const provisioning = deviceProvisioning ?? deviceQuery.data ?? null;
  const provisioningResolved = !shouldFetchProvisioning || deviceQuery.isSuccess;
  const canAssign = provisioningResolved && canAssignGatewayAdapter(provisioning);
  const assignmentBlocked = provisioningResolved && !canAssign;

  const resolvedAssignment = assignmentQuery.data ?? assignment ?? null;

  const efemRegistryQuery = useQuery({
    queryKey: ['gateway-adapter-registry', 'efem'],
    queryFn: () => providerGatewayAdaptersService.listGatewayAdapterRegistry('efem'),
    enabled: kind === 'efem',
  });

  const locationRegistryQuery = useQuery({
    queryKey: ['gateway-adapter-registry', 'location'],
    queryFn: () => providerGatewayAdaptersService.listGatewayAdapterRegistry('location'),
    enabled: kind === 'location',
  });

  const registryQuery = kind === 'efem' ? efemRegistryQuery : locationRegistryQuery;

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (kind === 'efem') {
        return providerGatewayAdaptersService.putGatewayAdapterAssignment('efem', serialNumber, {
          efemAdapterKey: adapterKey,
          notes: notes.trim() || undefined,
        });
      }
      return providerGatewayAdaptersService.putGatewayAdapterAssignment('location', serialNumber, {
        locationAdapterKey: adapterKey,
        notes: notes.trim() || undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assignmentQueryKey });
      void queryClient.invalidateQueries({ queryKey: ['efem-gateway-config-preview', serialNumber] });
      onAssigned?.();
    },
  });

  const entries = registryQuery.data?.data?.entries ?? [];
  const title = kind === 'efem' ? `${PRODUCT_NAMES.AXON} adapter assignment` : `${PRODUCT_NAMES.MATRIX} adapter assignment`;
  const keyLabel = kind === 'efem' ? `${PRODUCT_NAMES.AXON} adapter` : `${PRODUCT_NAMES.MATRIX} adapter`;
  const currentKey = adapterKeyFromAssignment(kind, resolvedAssignment);

  React.useEffect(() => {
    setAdapterKey(currentKey);
    setNotes(resolvedAssignment?.notes ?? '');
  }, [kind, serialNumber, currentKey, resolvedAssignment?.notes]);

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select the OEM adapter for this gateway. Assignment drives IoT thing groups, Greengrass
        deployment, and config push guardrails.
      </Typography>

      {assignmentQuery.isLoading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading assignment…</Typography>
        </Stack>
      )}

      {assignmentQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load adapter assignment:{' '}
          {assignmentQuery.error instanceof Error ? assignmentQuery.error.message : 'Unknown error'}
        </Alert>
      )}

      {resolvedAssignment && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Assigned: <strong>{currentKey}</strong> by {resolvedAssignment.assignedBy} at{' '}
          {new Date(resolvedAssignment.assignedAt).toLocaleString()}
        </Alert>
      )}

      {!assignmentQuery.isLoading && !resolvedAssignment && canAssign && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No adapter assigned. Assign an adapter before pushing gateway configuration.
        </Alert>
      )}

      {assignmentBlocked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Gateway is not provisioned. Provision this gateway before assigning an adapter.
        </Alert>
      )}

      {(shouldFetchProvisioning && deviceQuery.isLoading) && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Checking provisioning status…</Typography>
        </Stack>
      )}

      {registryQuery.isLoading && <CircularProgress size={24} />}
      {registryQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load adapter registry
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
        <AppTextField
          select
          label={keyLabel}
          value={adapterKey}
          onChange={(e) => setAdapterKey(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
          disabled={registryQuery.isLoading || entries.length === 0 || !canAssign}
        >
          {entries.map((e: { displayName: string; efemAdapterKey?: string; locationAdapterKey?: string }) => {
            const value = e.efemAdapterKey ?? e.locationAdapterKey ?? '';
            return (
              <MenuItem key={value} value={value}>
                {e.displayName} ({value})
              </MenuItem>
            );
          })}
        </AppTextField>
        <AppTextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          size="small"
          sx={{ minWidth: 200, flex: 1 }}
          disabled={!canAssign}
        />
        <Box>
          <AppButton
            variant="contained"
            disabled={!adapterKey || saveMutation.isPending || !canAssign}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? <CircularProgress size={22} /> : 'Save assignment'}
          </AppButton>
        </Box>
      </Stack>

      {saveMutation.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {saveMutation.error instanceof Error ? saveMutation.error.message : 'Save failed'}
        </Alert>
      )}
      {saveMutation.isSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Adapter assignment saved
        </Alert>
      )}
    </Box>
  );
}
